import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Mail, FileText, Heart, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/contexts/LanguageContext";
import { api } from "@/lib/api";

export default function Footer() {
    const { t, lang, setLang, supportedLanguages, translations } = useLang();
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const year = new Date().getFullYear();

    const onSubscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await api.subscribeNewsletter({ email, language: lang });
            toast.success(t.newsletter.success);
            setEmail("");
        } catch (err) {
            toast.error(t.newsletter.error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer data-testid="site-footer" className="relative mt-24 bg-caireen-ink text-caireen-cream overflow-hidden">
            {/* color stripe */}
            <div className="absolute top-0 inset-x-0 h-1.5 flex">
                <span className="flex-1 bg-caireen-olive" />
                <span className="flex-1 bg-caireen-yellow" />
                <span className="flex-1 bg-caireen-blue" />
                <span className="flex-1 bg-caireen-orange" />
                <span className="flex-1 bg-caireen-red" />
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-10 pt-20 pb-10 grid grid-cols-1 md:grid-cols-12 gap-12">
                {/* Brand + newsletter */}
                <div className="md:col-span-5 space-y-6">
                    <div className="flex items-center gap-3">
                        <span className="w-12 h-12 rounded-full overflow-hidden ring-2 ring-white/15">
                            <img src="/caireen-logo.jpg" alt="Caireen Verein" className="w-full h-full object-cover" />
                        </span>
                        <div className="leading-tight">
                            <p className="font-serif text-2xl">Caireen Verein</p>
                            <p className="font-sans text-xs tracking-[0.18em] uppercase text-caireen-cream/60">
                                Caireen e.V. · Egyesület
                            </p>
                        </div>
                    </div>
                    <p className="font-serif italic text-xl text-caireen-cream/85 max-w-md">
                        {t.footer.tagline}
                    </p>

                    <div className="pt-2">
                        <p className="font-sans text-sm uppercase tracking-[0.2em] text-caireen-cream/60 mb-3">
                            {t.newsletter.title}
                        </p>
                        <p className="font-sans text-sm text-caireen-cream/80 mb-4 max-w-md">
                            {t.newsletter.sub}
                        </p>
                        <form onSubmit={onSubscribe} className="flex items-center gap-2 max-w-md" data-testid="newsletter-form">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder={t.newsletter.email}
                                data-testid="newsletter-email"
                                className="flex-1 bg-white/5 border border-white/15 rounded-full px-5 py-3 font-sans text-sm placeholder:text-caireen-cream/40 focus:outline-none focus:ring-2 focus:ring-caireen-orange focus:border-transparent transition-all"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="newsletter-submit"
                                className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-caireen-orange hover:bg-caireen-red transition-colors disabled:opacity-60"
                            >
                                <Send className="w-4 h-4" strokeWidth={2.2} />
                            </button>
                        </form>
                    </div>
                </div>

                {/* Quick links */}
                <div className="md:col-span-3">
                    <p className="font-sans text-sm uppercase tracking-[0.2em] text-caireen-cream/60 mb-5">
                        {t.footer.quickLinks}
                    </p>
                    <ul className="space-y-3 font-sans text-sm">
                        {[
                            { to: "/about", label: t.nav.about },
                            { to: "/projects", label: t.nav.projects },
                            { to: "/news", label: t.nav.news },
                            { to: "/donate", label: t.nav.donate },
                            { to: "/contact", label: t.nav.contact },
                        ].map((l) => (
                            <li key={l.to}>
                                <Link
                                    to={l.to}
                                    data-testid={`footer-link-${l.to.replace("/", "") || "home"}`}
                                    className="inline-flex items-center gap-1.5 text-caireen-cream/80 hover:text-caireen-yellow transition-colors group"
                                >
                                    <ArrowRight className="w-3.5 h-3.5 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
                                    {l.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Contact info */}
                <div className="md:col-span-4">
                    <p className="font-sans text-sm uppercase tracking-[0.2em] text-caireen-cream/60 mb-5">
                        {t.footer.contactInfo}
                    </p>
                    <ul className="space-y-4 font-sans text-sm">
                        <li className="flex gap-3">
                            <MapPin className="w-4 h-4 mt-0.5 text-caireen-yellow shrink-0" strokeWidth={1.75} />
                            <span className="text-caireen-cream/85">{t.footer.address}</span>
                        </li>
                        <li className="flex gap-3">
                            <FileText className="w-4 h-4 mt-0.5 text-caireen-yellow shrink-0" strokeWidth={1.75} />
                            <span className="text-caireen-cream/85">ZVR 1121813363</span>
                        </li>
                        <li className="flex gap-3">
                            <Mail className="w-4 h-4 mt-0.5 text-caireen-yellow shrink-0" strokeWidth={1.75} />
                            <a href="mailto:office@caireen.at" className="text-caireen-cream/85 hover:text-caireen-yellow transition-colors">
                                office@caireen.at
                            </a>
                        </li>
                    </ul>

                    <div className="mt-8">
                        <p className="font-sans text-xs uppercase tracking-[0.2em] text-caireen-cream/60 mb-3">
                            {t.footer.languages}
                        </p>
                        <div className="flex items-center gap-2">
                            {supportedLanguages.map((code) => (
                                <button
                                    key={code}
                                    data-testid={`footer-lang-${code}`}
                                    onClick={() => setLang(code)}
                                    className={`px-3 py-1.5 rounded-full text-xs font-sans font-semibold tracking-wider transition-all ${
                                        lang === code
                                            ? "bg-caireen-orange text-white"
                                            : "border border-white/20 text-caireen-cream/70 hover:text-white hover:border-white/40"
                                    }`}
                                >
                                    {translations[code].meta.code}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    <p className="font-sans text-xs text-caireen-cream/60">
                        {t.footer.legal.replace("{year}", year)}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 font-sans text-xs text-caireen-cream/60">
                        <Link
                            to="/impressum"
                            data-testid="footer-impressum"
                            className="hover:text-caireen-yellow transition-colors underline-offset-2 hover:underline"
                        >
                            {t.footer.impressum}
                        </Link>
                        <span className="hidden md:inline text-caireen-cream/25">·</span>
                        <span className="inline-flex items-center gap-1.5">
                            Caireen Verein · ZVR 1121813363
                        </span>
                        <span className="hidden md:inline text-caireen-cream/25">·</span>
                        <span className="inline-flex items-center gap-1.5">
                            <Heart className="w-3.5 h-3.5 text-caireen-red" strokeWidth={2.4} fill="currentColor" />
                            9543 Arriach · Österreich
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    );
}
